import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Button,
  Alert,
  Spinner,
  Modal,
} from '@cloudscape-design/components';
import { FormJsonToggle, buildJsonFromForm } from '@features/configs/components/FormJsonToggle';
import { ConfigPreview } from '@features/configs/components/ConfigPreview';
import { UnsavedChangesGuard } from '@features/configs/components/UnsavedChangesGuard';
import { PendingDeletionWarning } from '@features/configs/components/PendingDeletionWarning';
import { useConfig, useUpdateConfig, useConfigs } from '@features/configs';
import { useDependents } from '@features/configs/hooks/use-dependents';
import { useNotifications } from '@features/notifications';

/**
 * Configuration edit page — pre-populated form with optimistic concurrency.
 * Requirements: 4.1–4.12, 6.5
 */
export function ConfigEditPage() {
  const { configId } = useParams<{ configId: string }>();
  const navigate = useNavigate();
  const { data: config, isLoading, error: fetchError } = useConfig(configId);
  const updateConfig = useUpdateConfig();
  const { addNotification } = useNotifications();
  const { data: dependentsData } = useDependents(configId);
  const { data: allConfigsData } = useConfigs();
  const allConfigs = allConfigsData?.items ?? [];

  const [configName, setConfigName] = useState('');
  const [configType, setConfigType] = useState('');
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [conflictError, setConflictError] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [saveCompleted, setSaveCompleted] = useState(false);

  // Track original values for dirty comparison
  const originalName = useRef('');
  const originalValues = useRef<Record<string, unknown>>({});

  // Pre-populate form when config loads
  useEffect(() => {
    if (config && !initialized) {
      setConfigName(config.configName);
      setConfigType(config.configType);
      setFormValues(config.payload ?? {});
      originalName.current = config.configName;
      originalValues.current = config.payload ?? {};
      setInitialized(true);
    }
  }, [config, initialized]);

  // Navigate away after save completes (isDirty is now false)
  useEffect(() => {
    if (saveCompleted) {
      navigate('/');
    }
  }, [saveCompleted, navigate]);

  // Compute dirty state by comparing current values to originals
  // After a successful save, force isDirty to false to allow navigation
  const isDirty =
    !saveCompleted &&
    initialized &&
    (configName !== originalName.current ||
      JSON.stringify(formValues) !== JSON.stringify(originalValues.current));

  // Track form value changes
  const handleFormValuesChange = (values: Record<string, unknown>) => {
    setFormValues(values);
  };

  const handleConfigNameChange = (name: string) => {
    setConfigName(name);
  };

  // Cancel with unsaved changes prompt
  const handleCancel = () => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      navigate('/');
    }
  };

  // Save with validation and optimistic concurrency
  const handleSave = async () => {
    if (!config || !configId) return;

    setValidationError(null);
    setConflictError(false);

    // Build final JSON for validation
    const finalJson = buildJsonFromForm(configId, configName, configType, formValues);

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(finalJson);
    } catch (e) {
      setValidationError(`Invalid JSON: ${e instanceof Error ? e.message : 'parse error'}`);
      return;
    }

    // Validate required fields
    const missing: string[] = [];
    if (!parsed.configName) missing.push('configName');
    if (!parsed.configType) missing.push('configType');
    if (!parsed.payload) missing.push('payload');
    if (missing.length > 0) {
      setValidationError(`Missing required field(s): ${missing.join(', ')}`);
      return;
    }

    // Validate size
    const size = new Blob([finalJson]).size;
    if (size > 400 * 1024) {
      setValidationError(`Configuration exceeds the 400KB size limit (${Math.round(size / 1024)}KB)`);
      return;
    }

    // Build payload
    const { _rawPayload, _businessUnit, ...payloadFields } = formValues;
    let payload: Record<string, unknown>;
    if (typeof _rawPayload === 'string') {
      try {
        payload = JSON.parse(_rawPayload);
      } catch {
        payload = {};
      }
    } else {
      payload = payloadFields;
    }

    try {
      await updateConfig.mutateAsync({
        configId,
        body: {
          configName,
          configType,
          payload,
          version: config.version,
          businessUnit: (_businessUnit as string) || undefined,
        },
      });
      // Reset originals so isDirty becomes false before navigation
      originalName.current = configName;
      originalValues.current = formValues;
      setSaveCompleted(true);
      addNotification('success', `Configuration '${configName}' updated successfully`);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      if (message.includes('modified by another user') || message.includes('409')) {
        setConflictError(true);
      } else {
        const { translateError } = await import('@features/notifications/error-messages');
        const translated = translateError(e, { configName });
        addNotification('error', translated.message, translated.technicalDetails);
      }
    }
  };

  if (isLoading || !initialized) {
    return (
      <ContentLayout>
        <Spinner size="large" />
      </ContentLayout>
    );
  }

  if (fetchError || !config) {
    return (
      <ContentLayout>
        <Alert type="error">{fetchError?.message ?? 'Configuration not found.'}</Alert>
      </ContentLayout>
    );
  }

  return (
    <UnsavedChangesGuard isDirty={isDirty}>
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={handleCancel}>Cancel</Button>
              <Button
                variant="primary"
                loading={updateConfig.isPending}
                onClick={handleSave}
              >
                Save
              </Button>
            </SpaceBetween>
          }
        >
          Edit: {config.configName}
        </Header>
      }
    >
      <SpaceBetween size="l">

        {/* BR-16: Warning if this config references a pending-deletion config */}
        {config && <PendingDeletionWarning config={config} allConfigs={allConfigs} />}

        {dependentsData && dependentsData.count > 0 && (
          <Alert type="info" dismissible>
            ℹ This configuration is referenced by {dependentsData.count} other configuration(s). Changes may affect:{' '}
            {dependentsData.dependents.map((d) => `${d.configName} (${d.configType})`).join(', ')}
          </Alert>
        )}

        {validationError && (
          <Alert type="error" dismissible onDismiss={() => setValidationError(null)}>
            {validationError}
          </Alert>
        )}

        {conflictError && (
          <Alert type="warning" action={<Button onClick={() => window.location.reload()}>Reload</Button>}>
            This configuration was modified by another user since you opened it. Reload to see the latest version.
          </Alert>
        )}

        <FormJsonToggle
          configType={configType}
          formValues={formValues}
          onFormValuesChange={handleFormValuesChange}
          configName={configName}
          onConfigNameChange={handleConfigNameChange}
          configId={configId ?? ''}
          onConfigIdChange={() => {
            // configId is immutable during edit — show error handled by GuidedForm
          }}
          errors={configId !== config.configId ? { configId: 'configId cannot be changed.' } : {}}
        />

        <ConfigPreview
          configId={configId ?? ''}
          configName={configName}
          configType={configType}
          formValues={formValues}
        />
      </SpaceBetween>

      {/* Discard unsaved changes modal */}
      <Modal
        visible={showDiscardModal}
        onDismiss={() => setShowDiscardModal(false)}
        header="Discard unsaved changes?"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setShowDiscardModal(false)}>Keep editing</Button>
            <Button variant="primary" onClick={() => navigate('/')}>
              Discard
            </Button>
          </SpaceBetween>
        }
      >
        You have unsaved changes. Are you sure you want to leave?
      </Modal>
    </ContentLayout>
    </UnsavedChangesGuard>
  );
}
